import { getDefaultStartEndDates } from "@/common/date.utilities";
import ASSETS from "../common/constants/controlRisks/assets.js";

// parameters that should be encoded. prevents accidental encoding
// add new parameters here
import ACCEPTED_PARAMS from "../common/constants/apiUtilities/acceptedParams.js";

// parameters that require complex encodings or transformations
// add complex handling in handlecomplexParams()
import COMPLEX_PARAMS from "../common/constants/apiUtilities/complexParams.js";
import lodashHas from "lodash/has";
import { hasMaritimeAccess } from "@/services/user/user.service";

export { _encodeAllURLParameters as encodeAllURLParameters };

const SEVERITY_RANGE = 10;

/**
  This function fully encodes a url given a parameter object defined below:

  baseEndpoint: base endpoint for your query
  params: parameters to be encoded
  isIncidents: flag for Incidents, which sometimes uses different logic
  misc: anything additional, added to the end of the URL
*/
const _encodeAllURLParameters = (paramsObject) => {
  let assembledQuery = createQueryFromParams(
    paramsObject.params,
    paramsObject.isIncidents,
    paramsObject.isTravelTracker
  );

  let endpoint = encodeURI(paramsObject.baseEndpoint);
  if (assembledQuery) {
    endpoint += assembledQuery;
  }
  if (paramsObject.misc) {
    endpoint += paramsObject.misc;
  }
  return endpoint;
};

const createQueryFromParams = (params, isIncidents, isTravelTracker) => {
  // if no parameters to encode
  if (!Object.keys(params).length) {
    return "";
  }
  let query = "?";
  for (const prop in params) {
    // default encoder for generic, defined parameters that aren't complex parameters
    if (
      ACCEPTED_PARAMS.includes(prop) &&
      (params[prop] || params[prop] === 0) &&
      !COMPLEX_PARAMS.includes(prop)
    ) {
      query += `&${prop}=${encodeURI(params[prop])}`;
    }
  }
  // encoding for complex parameters
  query += handlecomplexParams(params, isIncidents, isTravelTracker);
  return query;
};

const handlecomplexParams = (params, isIncidents) => {
  let query = "";

  if (!params.noStartEnd && !isIncidents) {
    const START_END_DATES = getDefaultStartEndDates();
    if (params.end) {
      query += "&end=" + encodeURI(params.end);
    }

    let startParam = START_END_DATES.start;
    if (params.start) {
      startParam = params.start;
    }
    query += "&start=" + encodeURI(startParam);
  }
  if (params.bounds) {
    // handle bounds in separate function
    query += handleBounds(params, isIncidents);
  }
  if (params.hasOwnProperty("pageOffset")) {
    query += "&pageOffset=" + encodeURI(params.pageOffset);
  }
  if (params.hasOwnProperty("pageSize")) {
    query += "&pageSize=" + encodeURI(params.pageSize);
  }
  if (params.hasOwnProperty("fullDoc")) {
    query += "&fullDoc=" + encodeURI(params.fullDoc.toString());
  }
  if (params.search) {
    // search uses encodeURIComponent because it may contain #|&|@|;
    query += "&search=" + encodeURIComponent(params.search);
  }
  if (params.topicScores) {
    query += "&topicScores=" + encodeURI(JSON.stringify(params.topicScores));
  }
  if (params.color) {
    // color uses encodeURIComponent because it contains '#'
    query += "&color=" + encodeURIComponent(params.color);
  }
  if (params.topic) {
    params.topic = params.topic.replace("disasters", "disaster");
    query += "&topic=" + encodeURI(params.topic);
  }
  if (params.includeFilterCounts) {
    query += "&includeFilterCounts=true";
  }
  // grid complex parameters
  if (params.hasOwnProperty("local")) {
    query += "&useLocalScore=" + encodeURI(params.local);
  }
  if (
    params.varianceFilters &&
    Array.isArray(params.varianceFilters) &&
    params.varianceFilters.length
  ) {
    let rangeArr = [];
    for (let i = 0; i < params.varianceFilters.length; i++) {
      let vFilter = params.varianceFilters[i];
      if (vFilter.selected) {
        let filter = {
          id: vFilter.id,
        };
        if (vFilter.max || vFilter.max === 0) {
          filter.max = vFilter.max;
        }
        if (vFilter.min || vFilter.min === 0) {
          filter.min = vFilter.min;
        }
        rangeArr.push(filter);
      }
    }
    query += "&varianceFilter=" + encodeURIComponent(JSON.stringify(rangeArr));
  }
  if (params.omitGeometry) {
    query += "&omitGeometry=true";
  }
  if (params.syncTileScores) {
    query += "&syncTileScores=true";
  }
  // incidents complex parameters
  if (params.severityScores) {
    for (let i = 0; i <= SEVERITY_RANGE; i++) {
      if (
        params.severityScores &&
        params.severityScores[0] <= i &&
        params.severityScores[1] >= i
      ) {
        query += "&severityScores=" + i;
      }
    }
  }
  if (params.assetTypes) {
    params.assetTypes.forEach((asset) => {
      // if asset name is given, else id is given
      if (typeof asset === "string" || asset instanceof String) {
        query += "&assetTypes=" + encodeURI(asset);
      } else {
        let assetNameById = ASSETS.find((x) => x.Id === asset);
        if (assetNameById) {
          query += `&assetTypes=${encodeURI(assetNameById.Name)}`;
        }
      }
    });
  }
  if (params.categoriesId) {
    // list of category ids
    params.categoriesId.forEach((categorieId) => {
      query += "&categoriesId=" + categorieId;
    });
  }
  //TODO: This will go away supports current Verified Events Navigation
  if (params.sectors && !params.verifiedType) {
    // list of sector names
    params.sectors.forEach((sect) => {
      query += "&sectors=" + encodeURI(sect);
    });
  }
  //TODO: This will go away supports current Verified Events Navigation
  if (params.sectorsId) {
    // list of sector ids
    params.sectorsId.forEach((sect) => {
      query += "&sectorsId=" + sect;
    });
  }
  if (params.countriesCode) {
    params.countriesCode.forEach((code) => {
      query += "&countriesCode=" + encodeURI(code);
    });
  }

  if (params.eventType) {
    query += "&eventType=" + encodeURI(params.eventType);
  }

  if (params.verifiedType) {
    query += "&verifiedType=" + encodeURI(params.verifiedType);
    if (params.verifiedType === "security") {
      if (params.sectors) {
        // list of sector ids
        query += "&sectors=" + encodeURI(params.sectors);
      }
      if (params.assets) {
        // list of asset ids
        query += "&assets=" + encodeURI(params.assets);
      }
      if (params.attackTypes) {
        // list of attack types
        query += "&attackTypes=" + encodeURI(params.attackTypes);
      }
      if (params.perpetrators) {
        // list of perpetrators
        query += "&perpetrators=" + encodeURI(params.perpetrators);
      }
      if (lodashHas(params.severityMin)) {
        query += "&severityMin=" + encodeURI(params.severityMin);
      }
      if (lodashHas(params.severityMax)) {
        query += "&severityMax=" + encodeURI(params.severityMax);
      }
    } else if (params.verifiedType === "maritime" && hasMaritimeAccess()) {
      if (params.maritimeSubcategories) {
        // list of maritime subcategories
        query +=
          "&maritimeSubcategories=" + encodeURI(params.maritimeSubcategories);
      }
      if (params.maritimeVesselTypes) {
        // list of maritime vessel types
        query +=
          "&maritimeVesselTypes=" + encodeURI(params.maritimeVesselTypes);
      }
    }
  }

  if (params.riskCategories) {
    query += "&riskCategories=" + encodeURI(params.riskCategories);
  }

  if (lodashHas(params, "severityMin")) {
    query += "&severityMin=" + encodeURI(params.severityMin);
  }

  if (lodashHas(params, "severityMax")) {
    query += "&severityMax=" + encodeURI(params.severityMax);
  }

  if (lodashHas(params, "tags")) {
    query += "&tags=" + encodeURI(params.tags);
  }

  if (
    lodashHas(params, "geolocationPrecisions") &&
    Array.isArray(params.geolocationPrecisions) &&
    params.geolocationPrecisions.length > 0
  ) {
    query +=
      "&geolocationPrecisions=" + encodeURI(params.geolocationPrecisions);
  }

  return query;
};

const handleBounds = (params, isIncidents) => {
  let query = "";

  if (isIncidents) {
    let bounds = params.bounds.split(",");
    query += `&boundingBox.topLeft=${bounds[1]},${bounds[2]}`;
    query += `&boundingBox.bottomRight=${bounds[3]},${bounds[0]}`;
  } else {
    const regExBounds = new RegExp(
      "^-?[0-9]{1,7}(\\.[0-9]{0,7})?,\\s?-?[0-9]{1,7}(\\.[0-9]{0,7})?,\\s?-?[0-9]{1,7}(\\.[0-9]{0,7})?,\\s?-?[0-9]{1,7}(\\.[0-9]{0,7})?$"
    );
    if (!regExBounds.test(params.bounds)) {
      console.warn(
        "Possible issue (api.utilities.js): Bounds parameter must be in the form SWLat,SWLon,NELat,NELon and also precision of 7 digits or less",
        params.bounds
      );
      const PARTS = params.bounds.split(",");
      if (PARTS.length === 4) {
        let arr = [];
        PARTS.forEach((part) => {
          let value = part;
          // note: keepPrecision is used so that sometimes we don't want to cut off
          // the precision of the bounds such as when we are dealing with geohash.
          // There are times that a geohash is returned and then data is searched using
          // those bounds and if there's a single point within the bounds and the bounds
          // precision changes it may miss that one point inside of it.
          if (part.length > 7 && !params.keepPrecision) {
            value = parseFloat(part).toFixed(7);
          }
          arr.push(value);
        });
        params.bounds = arr.join();
        console.warn("Attempted to fix. New bounds are:", params.bounds);
      }
    }
    query += "&bounds=" + encodeURI(params.bounds);
  }
  return query;
};
